import React from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { RootState } from 'redux/schemas';

// components
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import TierBadge from 'athena/components/tier-badge';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { config } from '@config/pendo.config.json';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import VoiceList from './voice-list';

const styles = css`
  .opacity-50 {
    opacity: 0.5;
  }
`;

const AICharacterVoice = () => {
  const { setValue, watch } = useFormContext();
  const interactionMode = watch('interactionMode');
  const isVoiceEnabled = interactionMode === RolePlayInteractionMode.VOICE;
  const institution = useSelector(getCurrentInstitution);
  const isStarterPackage = !institution?.practicePlusEnabled;
  const params = useLecturePageParams();
  const courseAliases = useSelector((state: RootState) => getFlatCourseAliases(state, params.catalogId));

  return (
    <div css={styles} className='mt-6'>
      <div className='d-flex align-items-center'>
        <div className='bold text-large-body lh-25 mr-2'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.TITLE()}
        </div>
        <NvSwitch
          className='switch'
          checked={isVoiceEnabled}
          onChange={(checked) => setValue(
            'interactionMode',
            checked ? RolePlayInteractionMode.VOICE : RolePlayInteractionMode.TEXT,
            { shouldDirty: true },
          )}
          aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.TITLE()}
          data-qa={config.pendo.activities.aiRolePlay.configModal.editInteractionModeToggle}
        />
        {isStarterPackage && (
          <>
            <TierBadge
              className={`ml-2${!isVoiceEnabled ? ' opacity-50' : ''}`}
              label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.STARTER_BADGE()}
              dataQa={config.pendo.activities.aiRolePlay.configModal.starterBadge}
            />
            {isVoiceEnabled && (
              <NvTooltip
                placement='top'
                text={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.STARTER_TOOLTIP(courseAliases)}
              >
                <NvIcon
                  icon='info'
                  size='smallest'
                  className='ml-2 text-gray-2'
                  tabIndex={0}
                  altLabel={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.STARTER_TOOLTIP(courseAliases)}
                  data-qa={config.pendo.activities.aiRolePlay.configModal.starterPackageInfo}
                />
              </NvTooltip>
            )}
          </>
        )}
      </div>
      <div className='mt-1 text-gray-1 text-large-body lh-25'>
        {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.DESCRIPTION(courseAliases)}
      </div>

      {isVoiceEnabled && <VoiceList />}
    </div>
  );
};

export default AICharacterVoice;
